import { getSellerTypeParams, SellerType, UserDTO } from '@/types/other';
import { ShippingPolicy } from '@/types/skuDataBackend';
import isStringArrayDefined from '../is-string-array-defined';
import { countriesWithFlagsAndPrefix } from '@/constants/countries-with-flag';
import { ProfessionalSellerShareholder } from '@/types/api/api-v2';
import { ProfessionalSellerDataClient } from '@/types/user/user.types';

function getFulFillUserDataProps(user: UserDTO | null) {
  const userCity = user?.userAddress?.city
    ? user.userAddress.city[0].toUpperCase() +
      user.userAddress.city.slice(1, user.userAddress.city.length)
    : '';
  const userFirstName = user && user.firstName ? user.firstName : '';
  const userLastName = user && user.lastName ? user.lastName : '';
  const userDob = user && user.dob ? user.dob.split('T')[0] : '';
  const userLocale = user && user.local ? user.local : '';
  const userPhoneNumber =
    user && user.displayPhoneNumber ? user.displayPhoneNumber : '';
  const userCountryCode =
    user && user.userAddress && user.userAddress.countryCode
      ? user.userAddress.countryCode
      : '';
  const userLineOne =
    user && user.userAddress && user.userAddress.line1
      ? user.userAddress.line1
      : '';
  const userPostalCode =
    user && user.userAddress && user.userAddress.postalCode
      ? user.userAddress.postalCode
      : '';

  const { businessTaxId = null, businessName = null } = user?.Business || {};
  const isBowner = isStringArrayDefined([businessTaxId, businessName]);

  const userEmail: string = user?.email || '';
  const userBusinessName: string = user?.Business?.businessName || '';
  const userBusinessTaxId: string = user?.Business?.businessTaxId || '';
  const userIsProf: boolean = user?.isProffesional || false;

  return {
    userCity,
    userFirstName,
    userLastName,
    userDob,
    userLocale,
    userPhoneNumber,
    userCountryCode,
    userLineOne,
    userPostalCode,
    userEmail,
    userIsBusinessOwner: isBowner,
    userBusinessName,
    userBusinessTaxId,
    userIsProf,
  };
}

function getSellerType(params: getSellerTypeParams): SellerType {
  if (params.useUser === true) {
    const { isProffesional = false, specialProffesional = false } =
      params.user || {};

    if (specialProffesional) {
      return 'SPECIAL_PROFESSIONAL';
    }
    if (isProffesional) {
      return 'PROFESSIONAL';
    }
  }
  if (params.useUser === false) {
    if (params.isSpecialParam) {
      return 'SPECIAL_PROFESSIONAL';
    }
    if (params.isProfParam) {
      return 'PROFESSIONAL';
    }
  }

  return 'NORMAL';
}

function getSouldAllowChangesOnPolicy(params: getSellerTypeParams): boolean {
  return getSellerType(params) !== 'PROFESSIONAL';
}

function getHasCustomPolicy(shippingPolicy: ShippingPolicy | null | undefined) {
  const { custom: isCustomPolicy = false, carrier: carrierPolicy = null } =
    shippingPolicy ?? {};
  return isCustomPolicy && carrierPolicy !== 'DHL_GERMANY';
}

function getShouldShowDomestic(user: UserDTO | null | undefined): boolean {
  const {
    specialProffesional = false,
    AllowedCountries = [],
    allowedCountriesType = 'SELL_ALL',
    OriginAddress,
    Business,
    userAddress,
  } = user || {};

  if (!specialProffesional) {
    return true;
  }

  const countriesIso2 = AllowedCountries?.map((country) =>
    country.Country.iso2.toUpperCase()
  );
  const userAllowedType = allowedCountriesType;

  if (userAllowedType === 'SELL_ALL') {
    return true;
  }

  const userCountry =
    OriginAddress?.countryCode ||
    Business?.businessCountry ||
    userAddress?.countryCode;

  if (!userCountry) {
    return true;
  }

  const findCountry = countriesIso2?.find(
    (country) => country.toUpperCase() === userCountry?.toUpperCase()
  );

  return Boolean(findCountry);
}

function getIsUserLoading(
  isLoading: boolean,
  isManuallyRefetching: boolean,
  isPending: boolean,
  isRefetching: boolean,
  isLoadingSubmit: boolean
) {
  return (
    isLoading ||
    isLoadingSubmit ||
    isManuallyRefetching ||
    isPending ||
    isRefetching
  );
}

function parseUserPhoneNumberAction(phoneNumberParam: string) {
  const phoneNumber = phoneNumberParam.replace(/[^\d+]/g, '');
  const normalizedPhoneNumber = phoneNumberParam.replace(/[^\d]/g, '');

  const defaultPrefix = countriesWithFlagsAndPrefix.find(
    (country) => country.cc === 'DE'
  )!;

  const findPhonePrefix = countriesWithFlagsAndPrefix.find((country) => {
    const normalizedPrefix = country.phonePrefix.replace(/[^\d]/g, '');
    const match = normalizedPhoneNumber.startsWith(normalizedPrefix);
    return match;
  });

  const phonePrefix = findPhonePrefix || defaultPrefix;

  const escapedPrefix = phonePrefix.phonePrefix
    .trim()
    .replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const newPhoneNumber = phoneNumber
    // eslint-disable-next-line security/detect-non-literal-regexp
    .replace(new RegExp(`^(?:${escapedPrefix})+`), '')
    .trim();

  return {
    phonePrefix: {
      flag: phonePrefix.flag,
      prefix: phonePrefix.phonePrefix,
    },
    newPhoneNumber,
  };
}

function parseUserPhoneNumber(user: Partial<UserDTO>): UserDTO {
  const defaultPrefix = countriesWithFlagsAndPrefix.find(
    (country) => country.cc === 'DE'
  )!;
  const updatedUser = { ...user };

  if (user.shippingAddress?.phoneNumber) {
    const { newPhoneNumber, phonePrefix } = parseUserPhoneNumberAction(
      user.shippingAddress.phoneNumber
    );
    updatedUser.shippingAddress = {
      ...user.shippingAddress,
      phoneNumber: newPhoneNumber,
      displayPhoneNumber: newPhoneNumber,
      phonePrefix: {
        flag: phonePrefix.flag,
        prefix: phonePrefix.prefix,
      },
    };
  }

  if (user.phoneNumber) {
    const { newPhoneNumber, phonePrefix } = parseUserPhoneNumberAction(
      user.phoneNumber
    );
    const { newPhoneNumber: newDisplayPhoneNumber } =
      parseUserPhoneNumberAction(user.displayPhoneNumber || '');

    const resultUser = {
      ...updatedUser,
      phoneNumber: newPhoneNumber,
      displayPhoneNumber: newDisplayPhoneNumber,
      phonePrefix: {
        flag: phonePrefix.flag,
        prefix: phonePrefix.prefix,
      },
    } as UserDTO;

    return resultUser;
  }

  const fallbackUser = {
    ...updatedUser,
    phonePrefix: {
      flag: defaultPrefix.flag,
      prefix: defaultPrefix.phonePrefix,
    },
  } as UserDTO;

  return fallbackUser;
}

function isUserShareholder(
  shareholders: ProfessionalSellerShareholder[],
  profData: Partial<ProfessionalSellerDataClient> &
    Pick<
      ProfessionalSellerDataClient,
      | 'primeBusinessContactFirstname'
      | 'primeBusinessContactSecondname'
      | 'primeBusinessContactLastname'
    >
) {
  const userIsShareholder =
    shareholders.find(
      (shareholder) =>
        shareholder.firstName === profData.primeBusinessContactFirstname &&
        shareholder.middleName === profData.primeBusinessContactSecondname &&
        shareholder.lastName === profData.primeBusinessContactLastname
    ) !== undefined;

  return userIsShareholder;
}

export {
  getFulFillUserDataProps,
  getSellerType,
  getSouldAllowChangesOnPolicy,
  getHasCustomPolicy,
  getShouldShowDomestic,
  getIsUserLoading,
  parseUserPhoneNumber,
  parseUserPhoneNumberAction,
  isUserShareholder,
};
